import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLogo } from '../context/LogoContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { uploadLogo, deleteLogo } from '../lib/api';
import { Upload, Trash2, Image, Shield, CheckCircle, AlertCircle } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const { logoUrl, updateLogo, refreshLogo } = useLogo();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!preview) {
      toast.error('Please select an image first');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadLogo({ logo_data: preview });
      updateLogo(response.data.logo_url);
      setPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!logoUrl) return;

    try {
      await deleteLogo();
      updateLogo(null);
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization's branding and preferences
          </p>
        </div>

        {/* Logo Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-primary" />
              Organization Logo
            </CardTitle>
            <CardDescription>
              Upload your municipality or barangay logo. Recommended size: 200x60 pixels. Supports PNG, JPG, or SVG.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Logo Preview */}
            <div className="space-y-3">
              <Label>Current Logo</Label>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-md border border-slate-200 min-h-[80px]">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Organization logo" 
                    className="max-h-16 max-w-[200px] object-contain"
                    data-testid="current-logo"
                  />
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="h-8 w-8 text-primary" />
                    <div>
                      <p className="font-semibold text-foreground">Municipal Health</p>
                      <p className="text-xs">Default logo in use</p>
                    </div>
                  </div>
                )}
              </div>
              {logoUrl && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Logo
                </Button>
              )}
            </div>

            {/* Upload New Logo */}
            <div className="space-y-3 pt-4 border-t">
              <Label>Upload New Logo</Label>
              <div className="space-y-4">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleFileSelect}
                  className="cursor-pointer"
                  data-testid="logo-file-input"
                />
                
                {/* Preview of selected image */}
                {preview && (
                  <div className="space-y-3">
                    <div className="p-4 bg-slate-100 rounded-md border border-slate-200">
                      <p className="text-sm text-muted-foreground mb-2">Preview:</p>
                      <div className="bg-white p-3 rounded border inline-block">
                        <img 
                          src={preview} 
                          alt="Logo preview" 
                          className="max-h-16 max-w-[200px] object-contain"
                          data-testid="logo-preview"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleUpload}
                        disabled={uploading}
                        data-testid="upload-logo-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Save Logo'}
                      </Button>
                      <Button variant="outline" onClick={cancelPreview}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  For best results, use a logo with a transparent background. 
                  Maximum file size is 2MB.
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
